import React, { useMemo } from 'react';
import { Check } from 'lucide-react';
import { resolveLifecycle, TONE_CLASS, TONE_MARK } from '../shared/orderStatus';
import { formatShortDateTime } from '../shared/orderFormat';

/*
 * The order's position in its lifecycle, and the control that moves it.
 * Replaces the "Order Actions" card, which spent a card, a header, an icon and
 * a label on a single dropdown showing one value.
 *
 * Every label here comes from `admin/statuses`, so the stage names on the track
 * are the same words as the status pill in the header and the per-item pickers
 * below. Nothing is invented locally.
 */

const STATE_STYLE = {
  done: {
    label: 'text-[#3B4A54] font-semibold',
    track: TONE_MARK.success,
  },
  current: {
    label: 'text-nysmah-dark font-bold',
    track: TONE_MARK.progress,
  },
  upcoming: {
    label: 'text-[#7B8A93] font-medium',
    track: '#DFE6E9',
  },
};

const OrderLifecycle = ({
  statusId,
  lifecycle,
  statusLabel,
  stepTimestamps = {},
  isEditable,
  pendingStatusId,
  onSelect,
}) => {
  // Stages with a recorded timestamp are stages the order genuinely reached,
  // which is the only evidence available once it has exited the happy path.
  const reachedIds = useMemo(
    () =>
      new Set(
        Object.entries(stepTimestamps)
          .filter(([, at]) => Boolean(at))
          .map(([id]) => Number(id)),
      ),
    [stepTimestamps],
  );

  const { steps, terminal, held, offTrack } = resolveLifecycle({
    statusId,
    lifecycle,
    statusLabel,
    reachedIds,
  });

  if (!steps.length) return null;

  return (
    <section
      className="bg-white rounded-xl border border-[#D6DFE2] px-4 pt-3 pb-3.5 mb-4"
      aria-label="Order progress"
    >
      <div className="flex items-center justify-between gap-3 mb-2">
        <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#6B7C86]">
          {held ? 'Progress — paused' : 'Progress'}
        </span>
        {terminal && (
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[11.5px] font-bold rounded-full border ${TONE_CLASS[terminal.tone]}`}
          >
            {terminal.label}
          </span>
        )}
      </div>

      <ol className="flex gap-2 m-0 p-0 list-none">
        {steps.map((step) => {
          const style = STATE_STYLE[step.state];
          const timestamp = stepTimestamps[step.id];
          const isPending = pendingStatusId === step.id;
          const interactive =
            isEditable && !offTrack && step.state !== 'current';

          const body = (
            <>
              <span className="flex items-center gap-1.5">
                {step.state === 'done' && (
                  <Check
                    className="w-3.5 h-3.5 shrink-0"
                    style={{ color: TONE_MARK.success }}
                    strokeWidth={3}
                    aria-hidden="true"
                  />
                )}
                <span
                  className={`text-[13px] leading-tight ${style.label} ${
                    isPending ? 'opacity-60' : ''
                  }`}
                >
                  {step.label}
                </span>
              </span>
              <span className="block text-[11px] font-medium text-[#8A9BA8] tabular-nums leading-tight mt-1">
                {isPending
                  ? 'Saving…'
                  : timestamp
                    ? formatShortDateTime(timestamp)
                    : '—'}
              </span>
              <span
                className="block h-[5px] rounded-full mt-2"
                style={{
                  backgroundColor: offTrack ? '#DFE6E9' : style.track,
                }}
                aria-hidden="true"
              />
            </>
          );

          return (
            <li key={step.id} className="flex-1 min-w-0">
              {interactive ? (
                <button
                  type="button"
                  onClick={() => onSelect(step.id)}
                  disabled={isPending}
                  aria-label={`Mark order ${step.label}`}
                  className="block w-full text-left bg-transparent border-0 p-0 cursor-pointer rounded hover:opacity-75 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-nysmah-accent disabled:cursor-default"
                >
                  {body}
                </button>
              ) : (
                <div aria-current={step.state === 'current' ? 'step' : undefined}>
                  {body}
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
};

export default OrderLifecycle;
